import React, { FC, useState } from "react";
import styled from "@emotion/styled";
import { Select, Dialog, Text } from "@radix-ui/themes";
import { Button } from "@/components/button";
import { TERMINAL_FONT_FAMILY } from "./shared/constants";

interface ExtendTimeoutParams {
  timeout_seconds?: number;
}

interface ExtendTimeoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExtend: (params: ExtendTimeoutParams) => Promise<void>;
  isExtending: boolean;
}

const TIMEOUT_EXTENSION_OPTIONS = [
  { value: "10", label: "10 seconds" },
  { value: "30", label: "30 seconds" },
  { value: "60", label: "1 minute" },
  { value: "120", label: "2 minutes" },
  { value: "300", label: "5 minutes" },
  { value: "600", label: "10 minutes" },
] as const;

export const ExtendTimeoutModal: FC<ExtendTimeoutModalProps> = ({
  isOpen,
  onClose,
  onExtend,
  isExtending,
}) => {
  const [additionalSeconds, setAdditionalSeconds] = useState<string>("60");

  const handleSubmit = async () => {
    const params: ExtendTimeoutParams = {
      timeout_seconds: parseInt(additionalSeconds, 10),
    };

    await onExtend(params);
    setAdditionalSeconds("60");
    onClose();
  };

  const handleClose = () => {
    setAdditionalSeconds("60");
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Content style={{ maxWidth: "450px" }}>
        <DialogTitle>Extend Processing Timeout</DialogTitle>
        <DialogDescription size="2" mb="4">
          Extend the processing timeout to give the session more time to complete
        </DialogDescription>

        <FormContainer>
          <FormField>
            <Label>Additional Time</Label>
            <Select.Root value={additionalSeconds} onValueChange={setAdditionalSeconds}>
              <Select.Trigger placeholder="Select extension..." style={{ width: "100%" }} />
              <Select.Content>
                {TIMEOUT_EXTENSION_OPTIONS.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            <HelpText>How much additional time to give the session processing workflow.</HelpText>
          </FormField>

          <ActionButtons>
            <Button onClick={handleClose} disabled={isExtending}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isExtending}>
              {isExtending ? "Extending..." : "Extend Timeout"}
            </Button>
          </ActionButtons>
        </FormContainer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const DialogTitle = styled(Dialog.Title)`
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
`;

const DialogDescription = styled(Dialog.Description)`
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-12);
  font-family: ${TERMINAL_FONT_FAMILY};
`;

const HelpText = styled(Text)`
  font-size: 11px;
  color: var(--gray-10);
  font-family: ${TERMINAL_FONT_FAMILY};
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;
